// Shared helper to lazily mount a React feature into the global #app-component
// container. Centralizes dynamic imports of react/react-dom, the loading screen,
// the i18n boot, and the unmount/remount of window.appRoot.
//
// Usage from a pack:
//   mountReactFeature({
//     loader: () => import('../../react/containers/CreatePostContainer'),
//     props: { propertyId, ... },
//   })
//
// A feature with its own translation bundle passes an i18nLoader; it defaults to
// the create-post bundle so existing call sites keep working unchanged.

const startLoading = () => {
  const newDiv = document.createElement('div')
  newDiv.className = 'react-feature__loading-screen'
  const appComponent = document.getElementById('app-component')
  appComponent.parentNode.insertBefore(newDiv, appComponent.nextSibling)
}

const stopLoading = () => {
  document
    .querySelectorAll('.react-feature__loading-screen')
    .forEach((el) => el.remove())
}

export async function mountReactFeature({
  loader,
  props = {},
  i18nLoader = () => import('../react/locales/create-post/i18n'),
}) {
  startLoading()

  // Hide any open Bootstrap modal that triggered the click.
  $('.modal').modal('hide')

  const [{ default: React }, { createRoot }, { default: Component }, { default: i18n }] =
    await Promise.all([
      import('react'),
      import('react-dom/client'),
      loader(),
      i18nLoader(),
    ])

  const locale = document.documentElement.getAttribute('lang') || 'fr'
  await i18n.changeLanguage(locale)

  const appElement = document.getElementById('app-component')

  if (!window.appRoot) {
    window.appRoot = createRoot(appElement)
  }

  // Bump a mount id used as React key so reopening the same component type
  // remounts the tree (fresh effects + Recoil state) instead of reconciling.
  window.__reactFeatureMountId = (window.__reactFeatureMountId || 0) + 1

  try {
    window.appRoot.render(
      React.createElement(Component, { key: window.__reactFeatureMountId, ...props })
    )
  } finally {
    stopLoading()
  }

  $('body').addClass('modal-open')
}

// Tears the mounted feature down: the React tree is removed from the DOM so
// nothing it rendered can be targeted afterwards (Product Fruits hints keep
// anchoring onto a merely-hidden modal). The root itself is kept alive and
// reused by the next mountReactFeature call.
export function unmountReactFeature() {
  if (window.appRoot) window.appRoot.render(null)
}
